import { apiClient, ApiResponse } from '@api/apiClient';
import { V4ApiResponse } from '@api/types';
import { ToastType } from '@components/Toast/Toast';
import { Contact } from '@typeDefinitions/contactTypes';
import { FlagViewModel } from '@typeDefinitions/flagTypes';
import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';
import { logError } from '@utils/errorLogging';
import { createError } from '@utils/errorUtils';
import { handleToast } from '@utils/utils';
import {
  BounceCarrierPayload,
  Carrier,
  CarrierFeatures,
  CarrierInsuranceDetail,
  CommentViewModel,
  Playlist,
  RatePayloadType,
} from '@store/orders/types';

import { buildSourceTrackingConfig } from './serviceHelpers';

export async function fetchCarrierDetail(Id: number): Promise<Carrier> {
  try {
    const response = await apiClient.get(`carrier/details/${Id}`);

    return response as unknown as Carrier;
  } catch (error: unknown) {
    logError('fetchCarrierDetail', error as Error);
    throw createError(error, 'Failed to fetch carrier details info');
  }
}

export async function fetchCarrierFeatures(
  id: number,
): Promise<CarrierFeatures | undefined> {
  try {
    const response = await apiClient.get(
      `FeatureFlag/GetCarrierFeatures/${id}`,
    );
    return response as unknown as CarrierFeatures;
  } catch (error: unknown) {
    logError('fetchCarrierFeatures', error as Error);

    if (error instanceof Error) {
      handleToast(
        ToastType.Error,
        `Failed to fetch carrier features: ${error.message}`,
      );
    }
  }
}

export async function carrierUpdateInsurance(
  id?: number,
  changeTrackingSource?: ChangeTrackingSource,
) {
  try {
    if (!id) {
      throw new Error('Carrier ID is required to update insurance');
    }
    const response: V4ApiResponse<CarrierInsuranceDetail> =
      await apiClient.post(
        `carrier/updateinsurance?carrierId=${id}`,
        undefined,
        buildSourceTrackingConfig(changeTrackingSource),
      );
    return response;
  } catch (error: unknown) {
    logError('carrierUpdateInsurance', error as Error);
    throw createError(error, 'Failed to update carrier insurance');
  }
}

export async function addNewContactToCarrier(
  contact: Record<string, string | number | boolean | null>,
  changeTrackingSource?: ChangeTrackingSource,
) {
  try {
    const response: V4ApiResponse<Contact> = await apiClient.post(
      'Carrier/addcarriercontact',
      contact,
      buildSourceTrackingConfig(changeTrackingSource),
    );
    return response.value as unknown as Contact;
  } catch (error: unknown) {
    logError('addNewContactToCarrier', error as Error);
    throw createError(error, 'Failed to add contact info');
  }
}

export async function addFlag(
  option: Partial<FlagViewModel>,
  cb?: () => void,
  changeTrackingSource?: ChangeTrackingSource,
): Promise<FlagViewModel> {
  try {
    const response = await apiClient.post(
      'flag/CreateFlag',
      option,
      buildSourceTrackingConfig(changeTrackingSource),
    );

    return response as unknown as FlagViewModel;
  } catch (error: unknown) {
    logError('addFlag', error as Error);
    throw createError(error, 'Failed to add flag');
  } finally {
    cb && cb();
  }
}

export async function putFalloutCount(
  carrierId: number,
  changeTrackingSource?: ChangeTrackingSource,
) {
  try {
    const response: boolean = await apiClient.put(
      `carrier/fallout/${carrierId}`,
      undefined,
      buildSourceTrackingConfig(changeTrackingSource),
    );

    if (!response) {
      throw new Error('Failed to increment fallout count');
    }

    return response;
  } catch (error: unknown) {
    logError('putFalloutCount', error as Error);
    throw createError(error, 'Failed to add carrier details info');
  }
}

export async function bounceCarrier(
  payload: BounceCarrierPayload,
  changeTrackingSource?: ChangeTrackingSource,
) {
  try {
    await apiClient.post(
      '/Load/Bounce',
      payload,
      buildSourceTrackingConfig(changeTrackingSource),
    );
  } catch (error: unknown) {
    logError('bounceCarrier', error as Error);
    throw createError(error, 'Failed to bounce carrier');
  }
}

export async function updateFlag(
  option?: FlagViewModel,
  changeTrackingSource?: ChangeTrackingSource,
): Promise<FlagViewModel> {
  try {
    const response = await apiClient.put(
      'flag',
      option,
      buildSourceTrackingConfig(changeTrackingSource),
    );

    return response as unknown as FlagViewModel;
  } catch (error: unknown) {
    logError('updateFlag', error as Error);
    throw createError(error, 'Failed to update flag');
  }
}

export async function deleteFlag(
  id: number,
  comment?: string,
  changeTrackingSource?: ChangeTrackingSource,
) {
  try {
    const trackingConfig = buildSourceTrackingConfig(changeTrackingSource);
    const response = await apiClient.delete<ApiResponse<boolean>>(
      `flag/${id}`,
      {
        ...trackingConfig,
        data: { id: id, description: comment },
      },
    );
    if ('value' in response) {
      return response.value as boolean;
    }
    return false;
  } catch (error: unknown) {
    logError('deleteFlag', error as Error);
    throw createError(error, 'Failed to delete flag');
  }
}

export async function fetchPlaylists(): Promise<Playlist[] | undefined> {
  try {
    const response = await apiClient.get('playlist/getallplaylist');

    if ('value' in response) {
      return response.value as Playlist[];
    }

    throw new Error('Failed to fetch playlist');
  } catch (error: unknown) {
    logError('fetchPlaylists', error as Error);
    handleToast(ToastType.Error, JSON.stringify(error));
  }
}

export async function fetchPlaylistsById(
  id: number,
): Promise<Playlist | undefined> {
  try {
    const response = await apiClient.get(`playlist/${id}`);

    if ('value' in response) {
      return response.value as Playlist;
    }

    throw new Error('Failed to fetch playlist by id');
  } catch (error: unknown) {
    logError('fetchPlaylistsById', error as Error);
    handleToast(ToastType.Error, JSON.stringify(error));
  }
}

export async function createPlaylist(
  playlist: Playlist,
  changeTrackingSource?: ChangeTrackingSource,
): Promise<Playlist | undefined> {
  try {
    const response = await apiClient.post(
      'playlist',
      playlist,
      buildSourceTrackingConfig(changeTrackingSource),
    );
    if ('value' in response) {
      return response.value as Playlist;
    }
    throw new Error('Failed to create playlist');
  } catch (error: unknown) {
    logError('createPlaylist', error as Error);
    throw createError(error, 'Failed to create playlist');
  }
}

export async function addCarrierToPlaylist(
  carrierPlaylistPayload: {
    CarrierId: number;
    PlaylistId: number;
    Selected: boolean;
  },
  changeTrackingSource?: ChangeTrackingSource,
) {
  try {
    const response: { isSuccess: boolean } = await apiClient.post(
      'playlist/addcarrier',
      {
        ...carrierPlaylistPayload,
      },
      buildSourceTrackingConfig(changeTrackingSource),
    );
    if (response.isSuccess) {
      handleToast(ToastType.Success, 'Carrier Added to Playlist');
    } else {
      handleToast(ToastType.Error, 'Failed to add carrier to playlist');
    }
    return response;
  } catch (error: unknown) {
    logError('addCarrierToPlaylist', error as Error);
    throw createError(error, 'Failed to fetch playlist');
  }
}

export async function rateCarrier(
  { carrierId, loadId, rating, comment }: RatePayloadType,
  changeTrackingSource?: ChangeTrackingSource,
) {
  try {
    const response = await apiClient.post(
      'Carrier/AddCarrierRating',
      {
        id: carrierId,
        loadId: loadId,
        rating: rating,
        comment: comment,
      },
      buildSourceTrackingConfig(changeTrackingSource),
    );
    return response as unknown as Record<string, string | number>;
  } catch (error: unknown) {
    logError('rateCarrier', error as Error);
    throw createError(error, 'Failed to add rate');
  }
}

export const disputeFlag = async (
  flagId: number,
  name: string,
  changeTrackingSource?: ChangeTrackingSource,
) => {
  try {
    const res = await apiClient.post(
      `Flag/Dispute/${flagId}?name=${name}`,
      undefined,
      buildSourceTrackingConfig(changeTrackingSource),
    );
    return res as unknown as CommentViewModel | null;
  } catch (e) {
    logError('disputeFlag', e as Error);
    throw createError(e, `Failed to dispute the flag with flagId: ${flagId}`);
  }
};
